/**
 * When setup shows instead of the app, and in which mode. App checks this on every render, so it
 * lives apart from the wizard itself (src/pages/SetupWizard.tsx), which loads only when needed.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import type { SettingsView } from '../../shared/api';
import type { SetupMode } from './setup';

export interface SetupGate {
  mode: SetupMode;
  /** Where "Run setup again" starts: a "Set a goal" visit starts at what targets still need. */
  start: 'goal' | null;
  /** Where leaving goes ("Run setup again" and the preview): You, or Today for a "Set a goal" visit. */
  returnTo: string;
  /** Close setup (a first setup stays open after saving, until its last step is left). */
  close: () => void;
}

interface SetupState {
  start?: unknown;
  returnTo?: unknown;
}

/**
 * Whether setup shows instead of the app: a brand-new account (until it leaves the last step, even
 * once saved), or /setup and /setup/preview. Null while settings load, and otherwise.
 */
export function useSetupGate(settings: SettingsView | undefined): SetupGate | null {
  const location = useLocation();
  const [firstRun, setFirstRun] = useState(false);
  const needsSetup = settings ? !settings.account.onboarded : false;
  useEffect(() => {
    if (needsSetup) setFirstRun(true);
  }, [needsSetup]);
  if (!settings) return null;
  if (needsSetup || firstRun) return { mode: 'first', start: null, returnTo: '/', close: () => setFirstRun(false) };
  const state = (location.state ?? {}) as SetupState;
  const returnTo = typeof state.returnTo === 'string' && state.returnTo.startsWith('/') && !state.returnTo.startsWith('//') ? state.returnTo : '/you';
  const start = state.start === 'goal' ? 'goal' : null;
  if (location.pathname === '/setup') return { mode: 'again', start, returnTo, close: () => undefined };
  if (location.pathname === '/setup/preview') return { mode: 'preview', start: null, returnTo, close: () => undefined };
  return null;
}
